import './styles/index.css';
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import LauncherView from './components/LauncherView';
import { ProjectView } from './components/ProjectView';
import { EmbedView } from './components/EmbedView';
import { ConsoleView } from './components/ConsoleView';
import StorageManagement from './components/StorageManagement';
import { WarningDialog } from './components/WarningDialog';
import { MdsUrlWarningDialog } from './components/MdsUrlWarningDialog';
import { ExistingProjectDialog } from './components/ExistingProjectDialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorNotificationProvider } from './components/ErrorNotification';
import { NotificationSystem, useNotificationSystemInit } from './components/NotificationSystem';
import { ConfirmDialogSystem } from './components/ConfirmDialogSystem';
import { initializeGraphRegistry } from './components/graph-builder';
import { SmartHintContainer } from './components/ContextHelp';
import { duckdbProxy } from './utils/duckdb';
import { useMdsLoader } from './hooks/useMdsLoader';
import { useEmbedMode } from './hooks/useEmbedMode';
import { useConsoleMode } from './hooks/useConsoleMode';
import logger from './utils/logger';

// 開発環境でのみOPFS管理ツールを読み込み
if (import.meta.env.DEV) {
  import('./utils/opfsManager').then(() => {
    logger.info('OPFS management utilities loaded');
  });
}

/**
 * App - MIDASアプリケーションのメインコンポーネント
 *
 * Web版での変更点:
 * - React Routerを使用したSPAルーティング
 * - ランチャー画面とプロジェクト画面の切り替え
 * - データ読み込み時の警告メッセージ表示機能
 */

logger.info('App component loading...');

// Graph Builderレジストリを初期化（モジュールレベルで一度だけ実行）
initializeGraphRegistry();

// DuckDB Worker Proxyを初期化（バックグラウンドで非同期実行）
// E2Eテスト用：初期化完了をグローバル変数で公開
(window as Window & { duckdbInitPromise?: Promise<void> }).duckdbInitPromise = duckdbProxy.init().catch((error) => {
  logger.error('[App] DuckDB initialization failed', { error });
});

/**
 * ProjectRoute - ProjectView、EmbedView、ConsoleViewを切り替えるラッパーコンポーネント
 *
 * URLパラメータに応じて適切なビューを表示:
 * - `embed=true`: EmbedView（埋め込みモード）
 * - `mode=console`: ConsoleView（AIエージェント向けコンソールモード）
 * - それ以外: ProjectView（通常モード）
 */
function ProjectRoute() {
  const { isEmbedMode } = useEmbedMode();
  const { isConsoleMode } = useConsoleMode();

  if (isConsoleMode) {
    return <ConsoleView />;
  }

  if (isEmbedMode) {
    return <EmbedView />;
  }

  return <ProjectView />;
}

function AppContent() {
  const [searchParams] = useSearchParams();
  const [warningDialog, setWarningDialog] = useState<{
    warnings: string[];
    title: string;
    onContinue: () => void;
  } | null>(null);

  // 通知システムの初期化
  useNotificationSystemInit();

  // 埋め込みモードの検出
  const { isEmbedMode } = useEmbedMode();

  // URL経由のMDS読み込み（埋め込みモードでない場合のみ）
  const mdsLoader = useMdsLoader({ disabled: isEmbedMode });

  // カノニカルURLの設定（トラッキングパラメータを除外）
  useEffect(() => {
    const mds = searchParams.get('mds');

    if (mds) {
      // カノニカルURLを生成（mdsパラメータのみを含む）
      const canonicalParams = new URLSearchParams();
      canonicalParams.set('mds', mds);

      const canonicalUrl = `${window.location.origin}${window.location.pathname}?${canonicalParams.toString()}`;

      // 既存のcanonicalリンクを削除
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }

      // 新しいcanonicalリンクを追加
      const linkElement = document.createElement('link');
      linkElement.rel = 'canonical';
      linkElement.href = canonicalUrl;
      document.head.appendChild(linkElement);

      logger.info('[App] Canonical URL set:', canonicalUrl);

      // クリーンアップ
      return () => {
        if (document.querySelector('link[rel="canonical"]') === linkElement) {
          linkElement.remove();
        }
      };
    }
  }, [searchParams]);

  // グローバルな警告表示関数を設定
  // TODO: より良い方法（Context API等）に置き換え
  if (typeof window !== 'undefined') {
    window.showDataWarnings = (warnings: string[], title: string, onContinue?: () => void) => {
      setWarningDialog({
        warnings,
        title,
        onContinue: onContinue || (() => {})
      });
    };
  }

  const closeWarningDialog = () => {
    if (warningDialog?.onContinue) {
      warningDialog.onContinue();
    }
    setWarningDialog(null);
  };

  // 埋め込みモードはProjectRoute内で処理される（useParamsが必要なため）

  return (
    <>
      <Routes>
        {/* ランチャー画面（ホーム）/ 埋め込みモード */}
        <Route path="/" element={
          isEmbedMode ? <EmbedView /> : <LauncherView />
        } />

        {/* プロジェクト画面（Embedモード対応） */}
        <Route path="/project/:projectId" element={<ProjectRoute />} />

        {/* ストレージ管理画面 */}
        <Route path="/storage" element={<StorageManagement />} />

        {/* その他のパスは/にリダイレクト */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* 通知システム */}
      <NotificationSystem />

      {/* 確認ダイアログシステム */}
      <ConfirmDialogSystem />

      {/* スマートヒント */}
      <SmartHintContainer />

      {/* 警告ダイアログ */}
      {warningDialog && (
        <WarningDialog
          warnings={warningDialog.warnings}
          title={warningDialog.title}
          onClose={closeWarningDialog}
          onContinue={closeWarningDialog}
        />
      )}

      {/* MDS URL警告ダイアログ */}
      {mdsLoader.showWarning && mdsLoader.warningUrl && mdsLoader.warningReason && (
        <MdsUrlWarningDialog
          url={mdsLoader.warningUrl}
          reason={mdsLoader.warningReason}
          onCancel={mdsLoader.handleCancelWarning}
          onContinue={mdsLoader.handleContinueWarning}
        />
      )}

      {/* 既存プロジェクト確認ダイアログ */}
      {mdsLoader.showExistingProjectDialog && mdsLoader.existingProject && (
        <ExistingProjectDialog
          isOpen={mdsLoader.showExistingProjectDialog}
          projectInfo={mdsLoader.existingProject}
          onOpenExisting={mdsLoader.handleOpenExisting}
          onOpenNew={mdsLoader.handleOpenNew}
          onCancel={mdsLoader.handleCancelExistingDialog}
        />
      )}

      {/* MDS読み込みエラー表示 */}
      {mdsLoader.error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#fee',
          border: '1px solid #f88',
          borderRadius: '6px',
          padding: '16px',
          maxWidth: '400px',
          zIndex: 10000,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#c00' }}>
            Error Loading MDS File
          </div>
          <div style={{ fontSize: '14px', color: '#333' }}>
            {mdsLoader.error}
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorNotificationProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ErrorBoundary>
    </ErrorNotificationProvider>
  );
}

export default App;